import { DateTime } from "luxon";
import moment from "moment-timezone";
import { getUserTimeZone } from "../../utils/user-details";
import toaster, { Theme } from "@saleshandy/designs/lib/toaster";

export const getDateObj = (start: string, end: string) => ({
  startDateObj: start ? new Date(start) : null,
  endDateObj: end ? new Date(end) : null,
});

export const isValidRange = (startDate, endDate) => {
  const daysDiff = moment(new Date(endDate)).diff(
    moment(new Date(startDate)),
    "days"
  );

  return daysDiff <= 90;
};

export const setCurrentTimestamp = (dateToChange) => {
  if (!dateToChange) {
    return new Date();
  }

  const currentBrowserTimeStamp = new Date();
  const currentHour = currentBrowserTimeStamp.getHours();
  const currentMinute = currentBrowserTimeStamp.getMinutes();
  const currentSecond = currentBrowserTimeStamp.getSeconds();
  return new Date(
    new Date(dateToChange).setHours(currentHour, currentMinute, currentSecond)
  );
};

export const handleDateRangeChange = ({
  value,
  isCustom = false,
  setDateFilter,
  onDateTimeSelectionChange,
  customDurationOptions,
  shouldHaveRangeLimit = false,
  shouldHaveOneYearLimit = false,
}) => {
  const timeZone = getUserTimeZone();

  if (isCustom) {
    // Handle partial date selection (when only start date is selected)
    if (value && value[0] && !value[1]) {
      setDateFilter((prevState) => ({
        ...prevState,
        selectedDateRangeOption: "custom",
        startDate: DateTime.fromJSDate(setCurrentTimestamp(value[0])).setZone(
          timeZone
        ),
        endDate: null,
        displayText: "Custom",
      }));
      return;
    }

    // Handle complete date range selection
    if (!value || !value[0]) {
      return;
    }

    const fetch = !!(value[0] && value[1]);

    if (shouldHaveOneYearLimit && value[0] && value[1]) {
      const start = moment(new Date(value[0]));
      const end = moment(new Date(value[1]));
      const diffInDays = end.diff(start, "days");

      if (diffInDays > 365) {
        setDateFilter((prevState) => ({
          ...prevState,
          selectedDateRangeOption: "custom",
          startDate: new Date(),
          endDate: null,
          displayText: "Custom",
        }));

        toaster.error("Date range cannot exceed one year!", {
          theme: Theme.New,
        });

        return;
      }
    }

    if (!isValidRange(value[0], value[1]) && shouldHaveRangeLimit) {
      setDateFilter((prevState) => ({
        ...prevState,
        selectedDateRangeOption: "custom",
        startDate: new Date(),
        endDate: null,
        displayText: "Custom",
      }));

      toaster.error("Date range should not exceed 90 days!", {
        theme: Theme.New,
      });

      return;
    }

    setDateFilter((prevState) => ({
      ...prevState,
      selectedDateRangeOption: "custom",
      startDate: DateTime.fromJSDate(setCurrentTimestamp(value[0])).setZone(
        timeZone
      ),
      endDate:
        value[1] &&
        DateTime.fromJSDate(setCurrentTimestamp(value[1])).setZone(timeZone),
      displayText: "Custom",
    }));

    // Only call onDateTimeSelectionChange when both dates are selected
    if (fetch) {
      const startDate = DateTime.fromJSDate(
        setCurrentTimestamp(value[0])
      ).setZone(timeZone);
      const endDate = DateTime.fromJSDate(
        setCurrentTimestamp(value[1])
      ).setZone(timeZone);

      onDateTimeSelectionChange(
        "custom",
        startDate,
        value[1] && endDate,
        "Custom"
      );
    }
  } else {
    const selectedDate = customDurationOptions.find((d) => d.label === value);

    setDateFilter((prevState) => ({
      ...prevState,
      selectedDateRangeOption: value,
      startDate: selectedDate.startDate.setZone(timeZone),
      endDate: selectedDate.endDate.setZone(timeZone),
      displayText: selectedDate.displayText,
    }));

    onDateTimeSelectionChange(
      value,
      selectedDate.startDate.setZone(timeZone),
      selectedDate.endDate.setZone(timeZone),
      selectedDate.displayText
    );
  }
};

export const handleOnSelect = ({
  value,
  isCustom,
  isDatePickerOpen,
  setIsDatePickerOpen,
  onDateRangeChangeHandler,
}) => {
  if (isCustom) {
    // For custom selection, open the date picker
    setIsDatePickerOpen(true);
  } else {
    // For predefined options, close date picker and handle the selection
    setIsDatePickerOpen(false);
    onDateRangeChangeHandler(value, isCustom);
  }
};

export const handleClickOutside = ({
  event,
  dateFilterRef,
  setIsDatePickerOpen,
}) => {
  if (
    dateFilterRef &&
    dateFilterRef.current &&
    !dateFilterRef.current.contains(event.target)
  ) {
    setIsDatePickerOpen(false);
  }
};

export const formatDateRangeForDatePickerTooltip = (
  startDate: DateTime,
  endDate: DateTime,
  timeZone: string = "Asia/Kolkata"
): string => {
  const formattedStartDate = startDate.setZone(timeZone).toFormat("d LLL, yy");
  const formattedEndDate = endDate.setZone(timeZone).toFormat("d LLL, yy");

  const offset = startDate.setZone(timeZone).toFormat("ZZ");
  const gmtOffset = `GMT${offset}`;
  return `${formattedStartDate} - ${formattedEndDate}\n(${gmtOffset}) ${timeZone}`;
};
